import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { policies } from 'config/policies';

import { selectRole } from 'reduxLocal/slices/authSlice';

type PolicyKey = keyof typeof policies;
export type Policies = typeof policies;
export type PermitAction = Policies[PolicyKey][number];

/**
 * Provides methods to work with policies and permissions
 *
 * Returns {
 *   isPermitted(requestedAction) - method that check if the requested action is permitted by policies for the current user role
 * }
 *
 * Example:
 * const { isPermitted } = usePermissions();
 *
 * if(isPermitted('accounts:read')) return <Accounts/>;
 */

export const usePermissions = () => {
  const role = useSelector(selectRole) || 'default';
  const roleActions = (policies[role] || policies.default) as PermitAction[];

  const isPermitted = useCallback(
    (requestedAction: PermitAction): boolean => {
      // no actions for role
      if (!roleActions || roleActions.length === 0) {
        return false;
      }

      if (roleActions.includes(requestedAction)) {
        return true;
      }

      return false;
    },
    [roleActions]
  );

  return { isPermitted };
};
